/* eslint-disable no-unused-vars, jsx-a11y/anchor-is-valid, jsx-a11y/alt-text, eqeqeq, react-hooks/exhaustive-deps */
// MeetingRoomsUI.jsx — RON-5051 side-by-side meeting room availability view.
//
// Presentational only: it receives rooms/meetings already expressed in MINUTES
// from MeetingRoomsEvent.jsx and is responsible purely for turning those into
// pixels and reporting user intent back up (slot picked / meeting clicked).
import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import en from "date-fns/locale/en-GB";
import fr from "date-fns/locale/fr";
import es from "date-fns/locale/es";
import de from "date-fns/locale/de";
import it from "date-fns/locale/it";
import nl from "date-fns/locale/nl";
import pl from "date-fns/locale/pl";
import pt from "date-fns/locale/pt";
import cs from "date-fns/locale/cs";
import { useTranslation } from "react-i18next";

import "react-datepicker/dist/react-datepicker.css";

import { Images } from "../Common/Images.jsx";
import NotificationPanel from "../Common/Notification.jsx";
import Sidebar from "../Sidebar/Sidebar";
import { formatDateYYYYMMDD } from "../../utils/CommonUtils.js";
import useMeetingNameTooltip from "../../hooks/useMeetingNameTooltip.js";

registerLocale("en", en);
registerLocale("fr", fr);
registerLocale("es", es);
registerLocale("de", de);
registerLocale("it", it);
registerLocale("nl", nl);
registerLocale("pl", pl);
registerLocale("pt", pt);
registerLocale("cs", cs);

// Geometry contract — must stay in step with main-meetingrooms.css, where one
// hour label is 48px tall and the gridline repeats every 24px.
export const SLOT_MINUTES = 30;
export const PX_PER_SLOT = 24;
export const PX_PER_MINUTE = PX_PER_SLOT / SLOT_MINUTES; // 0.8
// Below this duration the default event padding alone exceeds the block
// height, so the compact variant is used instead.
const COMPACT_EVENT_MAX_MINUTES = 45;
// The participants row (icon + initials) needs ~20px on top of the title and
// time rows, which a block only has from 90 minutes (72px). Shorter meetings
// still list everyone in the hover tooltip.
const PARTICIPANTS_ROW_MIN_MINUTES = 90;
// Initials circles shown on the block before collapsing the rest into "+N".
// Room columns are a fixed 255px, leaving ~216px for this row after padding and
// the team icon. Circles are 18px and overlap by 5px (13px each after the first),
// so ~16 fit; 10 keeps clear room for a "+NN" circle on very large meetings.
const MAX_VISIBLE_INITIALS = 10;

const DATEPICKER_WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const FC_WEEKDAY_HEADER_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

// react-datepicker calls formatWeekDay once per column header in display order;
// with calendarStartDay={1} that order is a fixed Monday→Sunday.
const makeWeekdayHeaderFormatter = (t) => {
    let columnIndex = 0;
    return () => t(DATEPICKER_WEEKDAY_KEYS[columnIndex++ % 7]);
};

export const minutesToLabel = (minutes) => {
    const safe = Math.max(0, Number(minutes) || 0);
    const hours = Math.floor(safe / 60);
    const mins = safe % 60;
    return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
};

function MeetingRoomsUI({
    isLoading,
    isRoomsLoaded = true,
    rooms = [],
    gridStartMin = 480,
    gridEndMin = 1320,
    selectedDate,
    setSelectedDate,
    lastBookingDate,
    holidays = [],
    zones = [],
    zoneDropdownOpen,
    setZoneDropdownOpen,
    onZoneSelect,
    onMeetingClick,
    onSlotSelect,
    isThumbnailPresent,
    companyId,
}) {
    const { t } = useTranslation("common");
    const { i18n } = useTranslation();
    const datePickerLocale = i18n.language.split("-")[0];

    const zoneRootRef = useRef(null);
    const columnRefs = useRef({});

    const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [pickerMonth, setPickerMonth] = useState(selectedDate || new Date());
    const [dragState, setDragState] = useState(null);

    // Hover tooltip (name + time) on every meeting, shared with the week grid.
    // State rather than a ref: the grid only mounts once rooms arrive, and the
    // hook has to re-bind when it does.
    const [calendarEl, setCalendarEl] = useState(null);
    // Opens below the event, as in the design. Near the bottom of the window it
    // flips above, but never over the room-name header row (the upper bound).
    const getTooltipBoundsEl = useCallback(
        () => calendarEl?.querySelector(".resource-calendar-grid tbody") || null,
        [calendarEl]
    );
    useMeetingNameTooltip(calendarEl, {
        titleSelector: ".calendar-event",
        getBoundsEl: getTooltipBoundsEl,
        timeIcon: Images.TimeSlotClock,
        placement: "below",
        align: "tail-left",
    });

    const [UserFirstName] = useState(localStorage.getItem("firstname"));
    const headerTitle = t("react_hi").replace("%s", UserFirstName);

    const totalMinutes = Math.max(0, gridEndMin - gridStartMin);
    const gridHeightPx = totalMinutes * PX_PER_MINUTE;

    // One label per hour across the union of every room's booking window.
    const hourLabels = useMemo(() => {
        const labels = [];
        for (let minute = gridStartMin; minute < gridEndMin; minute += 60) {
            labels.push(minute);
        }
        return labels;
    }, [gridStartMin, gridEndMin]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (zoneRootRef.current && !zoneRootRef.current.contains(event.target)) {
                setZoneDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [setZoneDropdownOpen]);

    const isHolidayInList = (date) => holidays.includes(formatDateYYYYMMDD(date));

    const isSameMonthAndYear = (date1, date2) => {
        if (!date1 || !date2) return false;
        return (
            date1.getMonth() === date2.getMonth() &&
            date1.getFullYear() === date2.getFullYear()
        );
    };

    // "EEE dd MMM" built from the app's own i18n keys so every language —
    // including Irish, which has no Intl/date-fns data — renders consistently.
    const formatPickerInputValue = (date) => {
        if (!date) return "";
        const weekday = t(FC_WEEKDAY_HEADER_KEYS[date.getDay()]);
        const day = String(date.getDate()).padStart(2, "0");
        const englishMonth = new Intl.DateTimeFormat("en-US", { month: "long" })
            .format(date)
            .toLowerCase();
        return `${weekday} ${day} ${t(`month_3digit_of_${englishMonth}`)}`;
    };

    /* ------------------------------ drag to select ------------------------------ */

    // Snap a pointer position to the 30-minute slot it falls in.
    const minuteFromPointer = (clientY, element) => {
        if (!element) return null;
        const rect = element.getBoundingClientRect();
        const raw = gridStartMin + ((clientY - rect.top) / PX_PER_MINUTE);
        const snapped = Math.floor(raw / SLOT_MINUTES) * SLOT_MINUTES;
        return Math.min(Math.max(snapped, gridStartMin), gridEndMin - SLOT_MINUTES);
    };

    // A candidate range is only bookable if it clears every blocked range and
    // every existing meeting in that room.
    const isRangeFree = useCallback((room, startMin, endMin) => {
        const overlaps = (aStart, aEnd, bStart, bEnd) => aStart < bEnd && bStart < aEnd;

        const hitsUnavailable = (room.unavailableRanges || []).some((range) =>
            overlaps(startMin, endMin, range.startMin, range.endMin)
        );
        if (hitsUnavailable) return false;

        return !(room.meetings || []).some((meeting) =>
            overlaps(startMin, endMin, meeting.startMin, meeting.endMin)
        );
    }, []);

    const handlePointerDown = (event, room) => {
        // Existing events and blocked areas own their own interaction.
        if (event.target.closest(".calendar-event, .resource-unavailable")) return;
        if (event.button !== undefined && event.button !== 0) return;

        const element = columnRefs.current[room.spotId];
        const anchorMin = minuteFromPointer(event.clientY, element);
        if (anchorMin === null) return;

        event.currentTarget.setPointerCapture?.(event.pointerId);
        setDragState({
            spotId: room.spotId,
            anchorMin,
            startMin: anchorMin,
            endMin: anchorMin + SLOT_MINUTES,
        });
    };

    const handlePointerMove = (event, room) => {
        if (!dragState || dragState.spotId !== room.spotId) return;

        const currentMin = minuteFromPointer(event.clientY, columnRefs.current[room.spotId]);
        if (currentMin === null) return;

        setDragState((previous) => {
            if (!previous) return previous;
            return {
                ...previous,
                startMin: Math.min(previous.anchorMin, currentMin),
                endMin: Math.max(previous.anchorMin, currentMin) + SLOT_MINUTES,
            };
        });
    };

    const handlePointerUp = (event, room) => {
        if (!dragState || dragState.spotId !== room.spotId) return;

        const { startMin, endMin } = dragState;
        setDragState(null);

        if (!isRangeFree(room, startMin, endMin)) return;

        onSlotSelect?.({
            room,
            startMin,
            endMin,
            startTime: minutesToLabel(startMin),
            endTime: minutesToLabel(endMin),
        });
    };

    /* ---------------------------------- render ---------------------------------- */

    const renderMeeting = (room, meeting) => {
        const durationMinutes = Math.max(SLOT_MINUTES, meeting.endMin - meeting.startMin);
        const isCompact = durationMinutes <= COMPACT_EVENT_MAX_MINUTES;
        const participants = meeting.participants || [];
        const showParticipantsRow =
            participants.length > 0 && durationMinutes >= PARTICIPANTS_ROW_MIN_MINUTES;
        const hiddenParticipantCount = Math.max(0, participants.length - MAX_VISIBLE_INITIALS);

        return (

            <div
                key={meeting.id}
                role="button"
                tabIndex={0}
                className={`calendar-event fc-booking-event ${isCompact ? "" : ""}`}
                style={{
                    top: `${(meeting.startMin - gridStartMin) * PX_PER_MINUTE}px`,
                    height: `${durationMinutes * PX_PER_MINUTE}px`,
                }}
                // Read by useMeetingNameTooltip: the name (the block's own text also
                // holds the time and initials), the time row, and one row per
                // participant. No participants -> attribute omitted, no rows.
                data-tooltip-title={meeting.meetingName}
                data-tooltip-time={`${minutesToLabel(meeting.startMin)} - ${minutesToLabel(meeting.endMin)}`}
                data-tooltip-participants={
                    participants.length > 0 ? JSON.stringify(participants) : undefined
                }
                onClick={(event) => {
                    event.stopPropagation();
                    onMeetingClick?.({ room, meeting });
                }}
                onKeyDown={(event) => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    event.stopPropagation();
                    onMeetingClick?.({ room, meeting });
                }}
            >
                <div className="calendar-event-wrapper">
                    <div className="event-title-wrapper">
                    <p className="event-title">
                            {meeting.meetingName}  
                    </p>
                    </div>
                    {!isCompact && (
                        <div className="fc-booking-time">
                            <img src={Images.TimeSlotClock} />
                            {minutesToLabel(meeting.startMin)} - {minutesToLabel(meeting.endMin)}
                        </div>

                    )}
                    {showParticipantsRow && (
                        <div className="event-participants">
                            <img src={Images.TeamMembers} alt="" />
                            <div className="event-initials-stack">
                                {participants.slice(0, MAX_VISIBLE_INITIALS).map((participant, index) => (
                                    <span
                                        key={`${index}-${participant.initials}`}
                                        className="event-initials"
                                    >
                                        {participant.initials}
                                    </span>
                                ))}
                                {hiddenParticipantCount > 0 && (
                                    <span className="event-initials event-initials--more">
                                        +{hiddenParticipantCount}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

        );
    };

    return (
        <div>
            <div className="content-area">
                <header className="header">
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-md-2">
                                <div className="greeting-title">
                                    <p>{headerTitle}</p>
                                </div>
                            </div>

                            <div className="col-md-8">
                                <form action="" className="d-flex justify-content-center custom-inline-dropdown gap-3">
                                    <div className="filter-panel weekly-filter d-flex justify-content-end align-items-center gap-3">
                                        <div className="filter-options d-flex gap-3 w-100">
                                            <div className="booking-date header-date align-items-center d-flex">
                                                <label htmlFor="meetingrooms-datepicker">
                                                    <p className="sub-dropdown-name">{t("date")}</p>
                                                    <DatePicker
                                                        locale={datePickerLocale}
                                                        formatWeekDay={makeWeekdayHeaderFormatter(t)}
                                                        selected={isSameMonthAndYear(selectedDate, pickerMonth) ? selectedDate : null}
                                                        value={formatPickerInputValue(selectedDate)}
                                                        openToDate={pickerMonth}
                                                        open={isDatePickerOpen}
                                                        dateFormat="EEE dd MMM"
                                                        onChangeRaw={(e) => e.preventDefault()}
                                                        onInputClick={() => {
                                                            setPickerMonth(selectedDate || new Date());
                                                            setIsDatePickerOpen((prev) => !prev);
                                                        }}
                                                        onClickOutside={() => setIsDatePickerOpen(false)}
                                                        onCalendarClose={() => setIsDatePickerOpen(false)}
                                                        shouldCloseOnSelect={true}
                                                        onMonthChange={(date) => setPickerMonth(date)}
                                                        onChange={(date) => {
                                                            if (!date) return;
                                                            setSelectedDate(date);
                                                            setPickerMonth(date);
                                                            setIsDatePickerOpen(false);
                                                        }}
                                                        minDate={new Date()}
                                                        maxDate={lastBookingDate}
                                                        calendarStartDay={1}
                                                        dayClassName={(date) =>
                                                            isHolidayInList(date) ? "react-datepicker__day--holidayClass" : undefined
                                                        }
                                                    />
                                                </label>
                                            </div>

                                            <div
                                                ref={zoneRootRef}
                                                className="employee-list location-list"
                                                onClick={(e) => e.stopPropagation()}
                                                style={{ position: "relative", overflow: "visible", zIndex: 1 }}
                                            >
                                                <div className="dropdown form-input-container d-flex justify-content-center flex-column">
                                                    <button
                                                        className="dropdown-toggle position-relative custom-dropdown-btn"
                                                        type="button"
                                                        id="meetingrooms-zone-dropdown-btn"
                                                        onClick={() => setZoneDropdownOpen((s) => !s)}
                                                        aria-expanded={zoneDropdownOpen}
                                                    >
                                                        <p className="sub-dropdown-name">{t("zone")}</p>
                                                        <span>{t("react_all_meeting_rooms")}</span>
                                                    </button>

                                                    <ul
                                                        className={`dropdown-menu custom-dropdown ${zoneDropdownOpen ? "show" : ""}`}
                                                        aria-labelledby="meetingrooms-zone-dropdown-btn"
                                                        id="meetingrooms-zone-list"
                                                    >
                                                        {zones.map((zone) => (
                                                            <li
                                                                key={zone.Id}
                                                                onClick={() => {
                                                                    setZoneDropdownOpen(false);
                                                                    onZoneSelect?.(zone);
                                                                }}
                                                            >
                                                                <button type="button" className="dropdown-item">
                                                                    {zone.ZoneName}
                                                                </button>
                                                            </li>
                                                        ))}

                                                        {/* Divider + "All meeting rooms" (RON-5051): already the
                                                            active view here, so selecting it is a no-op. */}
                                                        <li className="zone-dropdown-divider" aria-hidden="true" />
                                                        <li className="selected">
                                                            <button
                                                                type="button"
                                                                className="dropdown-item"
                                                                onClick={() => setZoneDropdownOpen(false)}
                                                            >
                                                                {t("react_all_meeting_rooms")}
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>

                            <div className="col-md-2">
                                <div className="menu-bar-options d-flex justify-content-end gap-3">
                                    <div className="notification-info">
                                        <a
                                            href="#"
                                            data-bs-toggle="offcanvas"
                                            data-bs-target="#notification-panel"
                                            aria-controls="notification-panel"
                                            className="position-relative notification-icon"
                                        >
                                            <img src={Images.notification} alt="" />
                                            {hasUnreadNotifications && <span className="orange-dot"></span>}
                                        </a>
                                    </div>
                                    <NotificationPanel onUnreadChange={setHasUnreadNotifications} />
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                <div className="component-area">
                    {isLoading && (
                        <div id="preloader">
                            <img src={Images.LoaderFull} alt="" />
                        </div>
                    )}

                    <Sidebar IsThumbnailPresent={isThumbnailPresent} CompanyId={companyId} />

                    <div className="pt-96 my-container active-cont">
                        <div className="container">
                            <div className="p-4 pb-0 relative meetingrooms-view-sec">
                                {rooms.length === 0 ? (
                                    // Only once the rooms request has settled: before that,
                                    // `rooms` is just the initial [] and the loader is showing.
                                    isRoomsLoaded && !isLoading ? (
                                        <div className="no-rooms-found">
                                            <img src={Images.NoSpaceError} className="img-fluid mb-2" alt="" />
                                            <p className="fs-5">{t("meeting_room_not_found")}</p>
                                        </div>
                                    ) : null
                                ) : (
                                    <div id="calendar-meeting" className="resource-calendar" ref={setCalendarEl}>
                                        <div className="resource-calendar-grid">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th></th>
                                                        {rooms.map((room) => (
                                                            <th key={room.spotId}>
                                                                <div className="resource-label">
                                                                    <h6>{room.spotName}</h6>
                                                                    <span>{room.zoneName}</span>
                                                                </div>
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td>
                                                            {hourLabels.map((minute) => (
                                                                <div className="time-slot" key={minute}>
                                                                    {minutesToLabel(minute)}
                                                                </div>
                                                            ))}
                                                        </td>

                                                        {rooms.map((room) => (
                                                            <td
                                                                key={room.spotId}
                                                                className="resource-column"
                                                                ref={(element) => {
                                                                    columnRefs.current[room.spotId] = element;
                                                                }}
                                                                style={{ height: `${gridHeightPx}px` }}
                                                                onPointerDown={(event) => handlePointerDown(event, room)}
                                                                onPointerMove={(event) => handlePointerMove(event, room)}
                                                                onPointerUp={(event) => handlePointerUp(event, room)}
                                                                onPointerCancel={() => setDragState(null)}
                                                            >
                                                                {(room.unavailableRanges || []).map((range) => (
                                                                    <div
                                                                        key={`${room.spotId}-blocked-${range.startMin}-${range.endMin}`}
                                                                        className="resource-unavailable"
                                                                        style={{
                                                                            top: `${(range.startMin - gridStartMin) * PX_PER_MINUTE}px`,
                                                                            height: `${(range.endMin - range.startMin) * PX_PER_MINUTE}px`,
                                                                        }}
                                                                    />
                                                                ))}

                                                                {(room.meetings || []).map((meeting) => renderMeeting(room, meeting))}

                                                                {dragState?.spotId === room.spotId && (
                                                                    <div
                                                                        className="drag-selection"
                                                                        style={{
                                                                            top: `${(dragState.startMin - gridStartMin) * PX_PER_MINUTE}px`,
                                                                            height: `${(dragState.endMin - dragState.startMin) * PX_PER_MINUTE}px`,
                                                                        }}
                                                                    />
                                                                )}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MeetingRoomsUI;
